import { useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Select } from 'antd';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CalendarSkeleton,
  ChartCard,
  HBars,
  InfoIcon,
  toHex,
} from '@/components/profile';
import { DATE_FORMATS } from '@/utils/formUtils';

const MONTHS = Array.from({ length: 12 }, (_, i) => i);

// Share of the busiest day → one of five accent shades.
const shadeFor = (count, max) => {
  if (!count) return null;
  const level = Math.min(4, Math.ceil((count / Math.max(1, max)) * 4));
  return [0.2, 0.4, 0.6, 0.8, 1][level];
};

function AbsentCalendar({ year, stats, publicHolidays, tc, te }) {
  const holidaySet = useMemo(
    () =>
      new Set((publicHolidays || []).map((d) => dayjs(d).format('YYYY-MM-DD'))),
    [publicHolidays]
  );
  const counts = stats?.absentByDay || {};
  const max = Math.max(0, ...Object.values(counts));
  const todayStr = dayjs().format('YYYY-MM-DD');

  return (
    <div
      className="flex flex-col gap-[3px]"
      role="img"
      aria-label={tc('calendarTitle', { year })}
    >
      {MONTHS.map((m) => {
        const first = dayjs(new Date(year, m, 1));
        const dim = first.daysInMonth();
        return (
          <div key={m} className="flex items-center gap-[2px]">
            <span className="w-[30px] shrink-0 text-[10px] font-semibold capitalize text-zinc-500">
              {first.format('MMM')}
            </span>
            {Array.from({ length: 31 }, (_, i) => {
              const d = i + 1;
              if (d > dim)
                return <span key={d} className="h-[13px] min-w-0 flex-1" />;
              const date = first.date(d);
              const iso = date.format('YYYY-MM-DD');
              const label = date.format(DATE_FORMATS.DISPLAY);
              const weekend = date.day() === 0 || date.day() === 6;
              const count = counts[iso] || 0;
              const opacity = shadeFor(count, max);
              let cls = 'bg-zinc-100';
              let style;
              let tip = `${label} · ${tc('absentPeople', { count })}`;
              // A public holiday stays one even when people on longer leave
              // (sick, maternity) count as absent that day.
              if (holidaySet.has(iso)) {
                cls = 'bg-zinc-300';
                tip = `${label} ${te('legendPublicHoliday')}${count ? ` · ${tc('absentPeople', { count })}` : ''}`;
              } else if (opacity) {
                cls = '';
                style = {
                  backgroundColor: `rgba(var(--accentColor), ${opacity})`,
                };
              } else if (weekend) {
                cls = 'border border-zinc-100 bg-white';
                tip = `${label} ${te('weekend')}`;
              }
              if (iso === todayStr) {
                cls = 'bg-red-700';
                style = undefined;
                tip = `${label} ${te('legendToday')}`;
              }
              return (
                <span
                  key={d}
                  title={tip}
                  style={style}
                  className={`box-border h-[13px] min-w-0 flex-1 rounded-[3px] ${cls}`}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function MonthBars({ data, dataKeys, year }) {
  const rows = (data || []).map((b) => ({
    ...b,
    name: dayjs(new Date(year, b.month - 1, 1)).format('MMM'),
  }));
  return (
    <div className="h-[180px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 4, right: 4, left: -24, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
          <Tooltip />
          {dataKeys.map((k) => (
            <Bar
              key={k.key}
              dataKey={k.key}
              name={k.label}
              stackId="a"
              fill={k.color}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Middle column of the company profile: who is absent on which day of the
 * year, and one chart that follows the selected tile. Employees are scoped
 * like the employee list, so a hrOfficer sees only their sectors.
 */
function CompanyProfileStats({
  company,
  stats,
  statsLoading,
  statsError,
  year,
  onYearChange,
  selected,
}) {
  const { t } = useTranslation(['translations']);
  const tc = (key, opts) =>
    t(`companyProfile.${key}`, { ns: 'translations', ...opts });
  const te = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });

  const years = useMemo(() => {
    const now = dayjs().year();
    return [now + 1, now, now - 1, now - 2].filter((y) => y >= 2022);
  }, []);
  const types = stats?.types || [];
  const accent = 'rgb(var(--accentColor))';

  const chart = (() => {
    if (selected === 'zaposleni') {
      return (
        <ChartCard title={tc('hiresTitle')} sub={String(year)}>
          <MonthBars
            year={year}
            data={stats?.hiresByMonth}
            dataKeys={[
              { key: 'count', label: tc('tileEmployees'), color: accent },
            ]}
          />
        </ChartCard>
      );
    }
    if (selected === 'ugovori') {
      return (
        <ChartCard title={tc('expiriesTitle')} sub={String(year)}>
          <MonthBars
            year={year}
            data={stats?.expiriesByMonth}
            dataKeys={[
              { key: 'count', label: tc('tileContracts'), color: accent },
            ]}
          />
        </ChartCard>
      );
    }
    if (selected === 'hr') {
      const rows = (stats?.byType || []).map((x) => ({
        label: x.label,
        value: x.totalDays,
        color: toHex(x.color),
      }));
      return (
        <ChartCard
          title={tc('byTypeTitle')}
          sub={String(year)}
          info={te('infoAbsences')}
        >
          {rows.length ? (
            <HBars rows={rows} />
          ) : (
            <div className="text-sm text-zinc-500">
              {te('noAbsences', { year })}
            </div>
          )}
        </ChartCard>
      );
    }
    if (
      ['vreme', 'naknade', 'obracun', 'bonus', 'placanje'].includes(selected)
    ) {
      return (
        <ChartCard
          title={tc('byMonthTitle')}
          sub={String(year)}
          info={te('infoAbsences')}
        >
          <MonthBars
            year={year}
            data={stats?.byMonth}
            dataKeys={types.map((x) => ({
              key: x.key,
              label: x.label,
              color: toHex(x.color),
            }))}
          />
        </ChartCard>
      );
    }
    return (
      <ChartCard title={tc('bySectorTitle')}>
        <HBars
          rows={(stats?.bySector || []).map((x) => ({
            label: x.name || tc('noSector'),
            value: x.count,
          }))}
        />
      </ChartCard>
    );
  })();

  return (
    <section
      aria-label={tc('calendarTitle', { year })}
      className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-auto rounded-[14px] bg-white p-3"
    >
      <div className="flex flex-col gap-2.5 rounded-[14px] border border-zinc-200 bg-white px-4 py-3.5">
        <div className="flex flex-wrap items-end justify-between gap-2.5">
          <div>
            <h2 className="m-0 text-base font-extrabold">
              {tc('calendarTitle', { year })}
              <InfoIcon text={tc('infoCalendar')} />
            </h2>
            <div
              className={`text-xs text-zinc-500 ${statsLoading ? 'invisible' : ''}`}
            >
              {tc('calendarSummary', {
                count: Object.keys(stats?.absentByDay || {}).length,
              })}
              {(stats?.byType || [])
                .slice(0, 4)
                .map((x) => ` · ${x.label} ${x.totalDays}`)
                .join('')}
            </div>
          </div>
          <Select
            size="small"
            value={year}
            onChange={onYearChange}
            options={years.map((y) => ({ value: y, label: y }))}
            className="w-24"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3.5 text-xs text-zinc-600">
          {[0.2, 0.6, 1].map((o) => (
            <i
              key={o}
              className="inline-block h-2.5 w-2.5 rounded-[3px]"
              style={{ backgroundColor: `rgba(var(--accentColor), ${o})` }}
            />
          ))}
          <span>{tc('absentPeople', { count: '1+' })}</span>
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block h-2.5 w-2.5 rounded-[3px] bg-zinc-300" />
            {te('legendPublicHoliday')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block h-2.5 w-2.5 rounded-[3px] bg-red-700" />
            {te('legendToday')}
          </span>
        </div>

        {statsError && (
          <Alert type="warning" showIcon message={te('statsUnavailable')} />
        )}
        {statsLoading ? (
          <CalendarSkeleton />
        ) : (
          <AbsentCalendar
            year={year}
            stats={stats}
            publicHolidays={company?.publicHolidays}
            tc={tc}
            te={te}
          />
        )}
      </div>

      {company && !statsLoading && chart}
    </section>
  );
}

export default memo(CompanyProfileStats);
